//================================================================
//  Component: Add Contracting Entity - Child component of NewEnergyRequestForm.js
//================================================================

//  Purpose: new business unit form, creates a new Firestore Document

//  Properties:
//    - openForm = {useState, open & close the modal}
//    - setOpenForm = {useState, open & close the modal}
//    - formData = {useReducer, partner useReducer formData}
//    - setFormData = {useReducer, partner useReducer setFormData}

//  Example:
//
//    <AddContractingEntity
//      openForm={openForm}
//      setOpenForm={setOpenForm}
//      formData={formData}
//      setFormData={setFormData}
//    ></AddContractingEntity>    

//================================================================


//Libraries
import React, { useContext, useEffect, useReducer } from 'react';

//Contexts
import { GetUser, SetToast } from '../../../Library/GlobalContexts';

//Components

//Functions
import QueryDocument from '../../../Library/QueryDocument';

//Images
import InfoIcon from '../../../Components/Images/Icon_Info_Blue.svg';
import returnIcon from '../../../Components/Images/Icon_Return_White.svg';

//CSS


export default function AddContractingEntity({
    openForm,
    setOpenForm,
    formData,
    setFormData,
    disabled,
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const setToast = useContext(SetToast);

    // --------------------------------------------------
    //  useReducer
    // --------------------------------------------------

    const [modalData, setModalData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {

            'name': formData?.contractingentity?.type === 'existing' ? '' : formData.contractingentity?.name,
            'nameError': '',

            'abn': formData?.contractingentity?.type === 'existing' ? '' : formData.contractingentity?.abn,

            'streetaddressline1': formData?.contractingentity?.type === 'existing' ? '' : formData.contractingentity?.address?.streetaddressline1,
            'streetaddressline2': formData?.contractingentity?.type === 'existing' ? '' : formData.contractingentity?.address?.streetaddressline2,

            'city': formData?.contractingentity?.type === 'existing' ? '' : formData.contractingentity?.address?.city,

            'state': formData?.contractingentity?.type === 'existing' ? '' : formData.contractingentity?.address?.state,

            'postcode': formData?.contractingentity?.type === 'existing' ? '' : formData.contractingentity?.address?.postcode,
            'postcodeError': '',

            'givenname': formData?.contractingentity?.type === 'existing' ? '' : formData.contractingentity?.givenname,
            'givennameError': '',
            'surname': formData?.contractingentity?.type === 'existing' ? '' : formData.contractingentity?.surname,
            'surnameError': '',
            'emailaddress': formData?.contractingentity?.type === 'existing' ? '' : formData.contractingentity?.emailaddress,
            'emailaddressError': '',
            'contactnumber': formData?.contractingentity?.type === 'existing' ? '' : formData.contractingentity?.contactnumber,
            'contactnumberError': '',

            'formChanged': false,
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Check for an existing meter
    function handleLookup() {

        if (modalData.name.length === 0) return;

        QueryDocument('contractingentities', [
            ['name', '==', modalData.name]
        ]).then((documents) => {

            if (documents.length === 0) {

                setModalData({ 'nameError': '' });
                setToast({
                    'type': '', // info, pending, success, warning, error
                    'message': '',
                });

                return;

            }

            setModalData({ 'nameError': `Unable to add an existing contracting entity.` });
            setToast({
                'type': 'error', // info, pending, success, warning, error
                'message': `Unable to add an existing contracting entity.`,
            });

        }).catch((error) => {

            console.log('error', error);
            setToast({
                'type': 'error', // info, pending, success, warning, error
                'message': 'Failed to check for existing contracting entity. Try again later.',
            });

        });

    }

    // Check if Save New Contracting Entity button is disabled
    function isDisabled(modalData) {
        return modalData.name.length === 0 ||
            modalData.abn.length === 0 ||
            modalData.streetaddressline1.length === 0 ||
            modalData.city.length === 0 ||
            modalData.state.length === 0 ||
            modalData.postcode.length === 0 ||
            modalData.givenname.length === 0 ||
            modalData.surname.length === 0 ||
            modalData.emailaddress.length === 0 ||
            modalData.contactnumber.length === 0 ||
            modalData.nameError.length !== 0 ||
            modalData.formChanged === false
    }

    //------------------------------------------------------
    //  useEffect
    //------------------------------------------------------

    // onChange
    //  1. IF the 'businessunit' changes, we need to update the reference in 'contractingentity'
    useEffect(() => {

        if (formData?.businessunit?.id === undefined) return;
        if (formData.businessunit.id === '') return;

        formData.contractingentity.businessunit = {
            'id': formData.businessunit.id,
            'name': formData.businessunit.name,
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.businessunit.id]);

    // onChange
    //  1. Reset form, IF contractingentity is empty
    useEffect(() => {

        if (formData?.contractingentity?.id === undefined) return;
        if (formData.contractingentity.id.length > 0) return;

        // Reset Form
        setModalData({

            'name': '',
            'nameError': '',

            'abn': '',

            'streetaddressline1': '',
            'streetaddressline2': '',

            'city': '',

            'state': '',

            'postcode': '',
            'postcodeError': '',

            'givenname': '',
            'givennameError': '',
            'surname': '',
            'surnameError': '',
            'emailaddress': '',
            'emailaddressError': '',
            'contactnumber': '',
            'contactnumberError': '',

            'formChanged': false,

        });

        setOpenForm(false);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.contractingentity.id]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    // Close the Modal
    if (openForm === false) return null;

    return (

        <div className='flex flex-col justify-center bg-[#E7F0F4] px-4 py-3 rounded-md w-fit'>

            {/* ========================================= */}
            {/*     Header                                */}
            {/* ========================================= */}

            <div className='flex flex-row justify-between items-center'>

                <div className='flex flex-row gap-2 text-[#5E93AC] font-semibold items-center my-2'>
                    <img src={InfoIcon} alt='Info-Icon'></img>
                    New Contracting Entity
                </div>

            </div>

            {/* ========================================= */}
            {/*     Form                                  */}
            {/* ========================================= */}

            <div className='flex flex-col rounded w-fit gap-2'>

                <div className='flex flex-row gap-2'>

                    {/* Name */}
                    <div className='flex flex-col gap-1'>
                        <label className='font-semibold'>Name</label>
                        <input
                            className={modalData.nameError === '' ? 'Input-Field-Text' : 'Input-Field-Text-Error'}
                            onChange={(e) => setModalData({ 'name': e.target.value, 'formChanged': true })}
                            onBlur={() => handleLookup()}
                            placeholder='Jones Lang LaSalle (NSW) Pty Limited'
                            style={{ width: '300px' }}
                            value={modalData.name}
                            disabled={disabled}
                        ></input>
                    </div>

                    {/* ABN */}
                    <div className='flex flex-col gap-1'>
                        <label className='font-semibold'>ABN</label>
                        <input
                            className='Input-Field-Text'
                            onChange={(e) => setModalData({ 'abn': e.target.value, 'formChanged': true })}
                            onBlur={() => handleLookup()}
                            placeholder='51 824 753 556'
                            style={{ width: '220px' }}
                            value={modalData.abn}
                            disabled={disabled}
                        ></input>
                    </div>

                </div>

                {/* Address Line 1 */}
                <div className='flex flex-col gap-1'>
                    <label className='font-semibold'>Address Line 1</label>
                    <input
                        className='Input-Field-Text'
                        onChange={(e) => setModalData({ 'streetaddressline1': e.target.value, 'formChanged': true })}
                        placeholder='Street Address'
                        style={{ width: '528px' }}
                        value={modalData.streetaddressline1}
                        disabled={disabled}
                    ></input>
                </div>

                {/* Address Line 2 */}
                <div className='flex flex-col gap-1'>
                    <label className='font-semibold'>Address Line 2</label>
                    <input
                        className='Input-Field-Text'
                        onChange={(e) => setModalData({ 'streetaddressline2': e.target.value, 'formChanged': true })}
                        placeholder='Apartment, suite, building, floor, etc. (optional)'
                        style={{ width: '528px' }}
                        value={modalData.streetaddressline2}
                        disabled={disabled}
                    ></input>
                </div>

                <div className='flex flex-row gap-2'>

                    {/* City */}
                    <div className='flex flex-col gap-1'>
                        <label className='font-semibold'>City</label>
                        <input
                            className='Input-Field-Text'
                            onChange={(e) => setModalData({ 'city': e.target.value, 'formChanged': true })}
                            placeholder='Sydney'
                            style={{ width: '312px' }}
                            value={modalData.city}
                            disabled={disabled}
                        ></input>
                    </div>

                    {/* State */}
                    <div className='flex flex-col gap-1'>
                        <label className='font-semibold'>State</label>
                        <select
                            className='Input-Field-Select'
                            onChange={(e) => setModalData({ 'state': e.target.value, 'formChanged': true })}
                            style={{ width: '100px' }}
                            value={modalData.state}
                            disabled={disabled}
                        >
                            <option hidden value=''>-</option>
                            <option value='NSW'> NSW </option>
                            <option value='VIC'> VIC </option>
                            <option value='QLD'> QLD </option>
                            <option value='SA'> SA </option>
                            <option value='WA'> WA </option>
                            <option value='NT'> NT </option>
                            <option value='ACT'> ACT </option>
                            <option value='TAS'> TAS </option>
                        </select>
                    </div>

                    {/* Post Code */}
                    <div className='flex flex-col gap-1'>
                        <label className='font-semibold'>Post Code</label>
                        <input
                            className={modalData.postcodeError.length === 0 ? 'Input-Field-Text' : 'Input-Field-Text-Error'}
                            onChange={(e) => {

                                const value = e.target.value;

                                if (value.length > 4 || value.length < 4) {

                                    return setModalData({
                                        'postcode': e.target.value,
                                        'postcodeError': 'Not Valid'
                                    });

                                }

                                setModalData({
                                    'postcode': e.target.value,
                                    'postcodeError': '',
                                    'formChanged': true,
                                });

                            }}
                            placeholder='2000'
                            style={{ width: '100px' }}
                            type='number'
                            value={modalData.postcode}
                            disabled={disabled}
                        ></input>
                    </div>

                </div>

                <div className='flex flex-col gap-1'>
                    <label className='font-medium'>Who is the primary billing contact? <span className='text-[#C4314B]'>* </span></label>

                    <div className='flex flex-row gap-2'>

                        {/* First name */}
                        <div className='flex flex-col'>
                            <label className='font-medium'>First name</label>
                            <input
                                className={modalData.givennameError?.length === 0 ? 'Input-Field-Text' : 'Input-Field-Text-Error'}
                                onChange={(e) => setModalData({ 'givenname': e.target.value, 'formChanged': true })}
                                placeholder='Sally'
                                value={modalData.givenname}
                                disabled={disabled}
                                style={{ width: '250px' }}
                            ></input>
                        </div>

                        {/* Surname */}
                        <div className='flex flex-col'>
                            <label className='font-medium'>Surname</label>
                            <input
                                className={modalData.surnameError?.length === 0 ? 'Input-Field-Text' : 'Input-Field-Text-Error'}
                                onChange={(e) => setModalData({ 'surname': e.target.value, 'formChanged': true })}
                                placeholder='Smith'
                                value={modalData.surname}
                                disabled={disabled}
                                style={{ width: '270px' }}
                            ></input>
                        </div>

                    </div>
                    <div className='flex flex-row gap-2'>

                        {/* Phone */}
                        <div className='flex flex-col'>
                            <label className='font-medium'>Phone</label>
                            <input
                                className={modalData.contactnumberError?.length === 0 ? 'Input-Field-Text' : 'Input-Field-Text-Error'}
                                onChange={(e) => setModalData({ 'contactnumber': e.target.value, 'formChanged': true })}
                                placeholder='04xx xxx xxx'
                                value={modalData.contactnumber}
                                type='number'
                                disabled={disabled}
                                style={{ width: '250px' }}
                            ></input>
                        </div>

                        {/* Email Address */}
                        <div className='flex flex-col'>
                            <label className='font-medium'>Email Address</label>
                            <input
                                className={modalData.emailaddressError?.length === 0 ? 'Input-Field-Text' : 'Input-Field-Text-Error'}
                                onChange={(e) => setModalData({ 'emailaddress': e.target.value, 'formChanged': true })}
                                placeholder='sally.smith@lendlease.com'
                                value={modalData.emailaddress}
                                type='email'
                                disabled={disabled}
                                style={{ width: '270px' }}
                            ></input>
                        </div>

                    </div>


                </div>


                {/* ========================================= */}
                {/*     Buttons Container                     */}
                {/* ========================================= */}

                <div className='flex flex-row gap-2 justify-between'>

                    {/* ========================================= */}
                    {/*     Choose Existing CE Button             */}
                    {/* ========================================= */}

                    <button hidden={disabled} className='Primary-Button w-fit mt-3' onClick={() => {

                        // Reset children
                        setFormData({
                            'contractingentity': {
                                'type': 'existing', // new or existing

                                // ======== EXISTING & NEW ======== //
                                'id': '',
                                'name': '',
                
                                // ========   NEW    ======== //
                                'abn': '',
                                'businessunit': {},
                                'address': {
                                    'streetaddressline1': '',
                                    'streetaddressline2': '',
                                    'city': '',
                                    'state': '',
                                    'postcode': '',
                                },
                                'givenname': '',
                                'surname':  '',
                                'emailaddress': '',
                                'contactnumber': '',
                                
                                'createdby': '', // emailaddress
                                'created': new Date(),
                                'modifiedby': '', // emailaddress
                                'modified': '',
                                'status': 'active', // active or decommissioned
                            },
                            'trust': {
                                'type': 'existing', // new or existing

                                // ======== EXISTING & NEW ======== //
                                'id': '',
                                'name': '',
                
                                // ========   NEW    ======== //
                                'abn': '',
                                'contractingentity': {},
                                'address': {
                                    'streetaddressline1': '',
                                    'streetaddressline2': '',
                                    'city': '',
                                    'state': '',
                                    'postcode': '',
                                },
                                'createdby': '', // emailaddress
                                'created': new Date(),
                                'modifiedby': '', // emailaddress
                                'modified': '',
                                'status': 'active', // active or decommissioned
                            },
                            'fund': {
                                'type': 'existing', // new or existing

                                // ======== EXISTING & NEW ======== //
                                'id': '',
                                'name': '',
                
                                // ========   NEW    ======== //
                                'abn': '',
                                'trust': {},
                                'address': {
                                    'streetaddressline1': '',
                                    'streetaddressline2': '',
                                    'city': '',
                                    'state': '',
                                    'postcode': '',
                                },
                                'createdby': '', // emailaddress
                                'created': new Date(),
                                'modifiedby': '', // emailaddress
                                'modified': '',
                                'status': 'active', // active or decommissioned
                            }
                        });

                        // Reset Form
                        setModalData({

                            'name': '',
                            'nameError': '',

                            'abn': '',

                            'streetaddressline1': '',
                            'streetaddressline2': '',

                            'city': '',

                            'state': '',

                            'postcode': '',
                            'postcodeError': '',

                            'givenname': '',
                            'givennameError': '',
                            'surname': '',
                            'surnameError': '',
                            'emailaddress': '',
                            'emailaddressError': '',
                            'contactnumber': '',
                            'contactnumberError': '',

                            'formChanged': false,

                        });

                        setOpenForm(false);

                    }}>
                        <div className='flex flex-row gap-2'>
                            <img src={returnIcon} className='w-[20px]' alt='Return'></img>
                            Choose existing contracting entity
                        </div>
                    </button>

                    {/* ========================================= */}
                    {/*     Save Button                           */}
                    {/* ========================================= */}

                    <button hidden={disabled} disabled={isDisabled(modalData)} className='Primary-Button w-fit self-end mt-3' onClick={() => {

                        setModalData({
                            'formChanged': false
                        });

                        setFormData({

                            // Contracting Entity should populate the billing address
                            'primarybillingGivenname': modalData.givenname,
                            'primarybillingSurname': modalData.surname,
                            'primarybillingEmailaddress': modalData.emailaddress,
                            'primarybillingContactnumber': modalData.contactnumber,

                            'contractingentity': {
                                'type': 'new', // new or existing

                                'id': `ce-${Date.now()}`,
                                'name': modalData.name,
                                'abn': modalData.abn,
                                'businessunit': {
                                    'id': formData.businessunit.id,
                                    'name': formData.businessunit.name,
                                },
                                'address': {
                                    'streetaddressline1': modalData.streetaddressline1,
                                    'streetaddressline2': modalData.streetaddressline2,
                                    'city': modalData.city,
                                    'state': modalData.state,
                                    'postcode': modalData.postcode,
                                },
                                'givenname': modalData.givenname,
                                'surname': modalData.surname,
                                'emailaddress': modalData.emailaddress,
                                'contactnumber': modalData.contactnumber,

                                'createdby': getUser.emailaddress,
                                'created': new Date(),
                                'modifiedby': '',
                                'modified': '',
                                'status': 'active', // active or decommissioned
                            }
                        });

                        setToast({
                            'type': '', // info, pending, success, warning, error
                            'message': '',
                        });

                    }}>
                        {formData?.contractingentity?.type === 'new' && isDisabled(modalData) ? 'Saved' : 'Save'}
                    </button>

                </div>

            </div>

        </div>
    )
}
